/**
 * useFeatureLimitsQuery — TanStack Query wrapper for billing/limits.
 *
 * Wraps the GET /billing/status endpoint. Provides the same FeatureLimits
 * type and convenience check functions as the legacy useFeatureLimits hook,
 * but backed by TanStack Query caching instead of useState/useEffect.
 *
 * staleTime: 300_000 (5 minutes) — billing status is session-scoped data
 * that rarely changes.
 */

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { queryKeys } from './queryKeys';
import { unwrapResponse } from '../../utils/unwrapResponse';

export interface FeatureLimits {
  tasks: {
    current: number;
    limit: number;
    unlimited: boolean;
  };
  storage: {
    current: number;
    limit: number;
    unlimited: boolean;
  };
  members: {
    current: number;
    limit: number;
    unlimited: boolean;
  };
  plan: 'free' | 'core' | 'pro' | 'leader' | 'owner';
  graceTier: string | null;
  graceExpiresAt: string | null;
  canCreateTask: boolean;
  canUploadMedia: boolean;
  canInviteMember: boolean;
}

const DEFAULT_LIMITS: FeatureLimits = {
  tasks: { current: 0, limit: 50, unlimited: false },
  storage: { current: 0, limit: 100 * 1024 * 1024, unlimited: false },
  members: { current: 0, limit: 2, unlimited: false },
  plan: 'free',
  graceTier: null,
  graceExpiresAt: null,
  canCreateTask: true,
  canUploadMedia: true,
  canInviteMember: true,
};

function parseSubscription(subscription: Record<string, unknown>): FeatureLimits {
  const limits = subscription.limits as Record<string, unknown> | undefined;
  const usage = subscription.usage as Record<string, unknown> | undefined;
  const plan = subscription.plan as string | { slug: string } | undefined;

  const taskLimit = limits?.tasks as number | null | undefined;
  const storageLimit = limits?.storage_bytes as number | null | undefined;
  const memberLimit = limits?.members as number | null | undefined;
  const planSlug = (typeof plan === 'object' && plan !== null ? plan.slug : plan) || 'free';

  const isTaskUnlimited = taskLimit === null || taskLimit === -1 || planSlug === 'pro' || planSlug === 'leader' || planSlug === 'owner';
  const isStorageUnlimited = storageLimit === null || storageLimit === -1 || planSlug === 'leader' || planSlug === 'owner';
  const isMemberUnlimited = memberLimit === null || memberLimit === -1 || planSlug === 'pro' || planSlug === 'leader' || planSlug === 'owner';

  return {
    tasks: {
      current: (usage?.tasks as number) || 0,
      limit: isTaskUnlimited ? Infinity : ((taskLimit as number) || 50),
      unlimited: isTaskUnlimited,
    },
    storage: {
      current: (usage?.storage_bytes as number) || 0,
      limit: isStorageUnlimited ? Infinity : ((storageLimit as number) || 100 * 1024 * 1024),
      unlimited: isStorageUnlimited,
    },
    members: {
      current: (usage?.members as number) || 0,
      limit: isMemberUnlimited ? Infinity : ((memberLimit as number) || 2),
      unlimited: isMemberUnlimited,
    },
    plan: planSlug as FeatureLimits['plan'],
    graceTier: (subscription.grace_tier as string) || null,
    graceExpiresAt: (subscription.grace_expires_at as string) || null,
    canCreateTask: (subscription.can_create_task as boolean) !== false,
    canUploadMedia: (subscription.can_upload_media as boolean) !== false,
    canInviteMember: (subscription.can_invite_member as boolean) !== false,
  };
}

/**
 * Fetch billing status and feature limits via TanStack Query.
 */
export function useFeatureLimitsQuery() {
  const queryClient = useQueryClient();

  const query = useQuery<FeatureLimits>({
    queryKey: queryKeys.featureLimits.status(),
    queryFn: async () => {
      try {
        const response = await api.get('/billing/status');
        return parseSubscription(unwrapResponse<any>(response.data));
      } catch {
        // Fallback to defaults on error (same as legacy hook)
        return DEFAULT_LIMITS;
      }
    },
    staleTime: 300_000,
  });

  const limits = query.data ?? null;

  const refreshLimits = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.featureLimits.status() });
  }, [queryClient]);

  const checkTaskLimit = useCallback((): boolean => {
    if (!limits) return true;
    if (limits.tasks.unlimited) return true;
    return limits.tasks.current < limits.tasks.limit;
  }, [limits]);

  const checkStorageLimit = useCallback(
    (sizeBytes: number): boolean => {
      if (!limits) return true;
      if (limits.storage.unlimited) return true;
      return limits.storage.current + sizeBytes <= limits.storage.limit;
    },
    [limits]
  );

  const checkMemberLimit = useCallback((): boolean => {
    if (!limits) return true;
    if (limits.members.unlimited) return true;
    return limits.members.current < limits.members.limit;
  }, [limits]);

  return {
    limits,
    loading: query.isLoading,
    error: query.error ? (query.error instanceof Error ? query.error.message : 'Failed to load subscription info') : null,
    refreshLimits,
    checkTaskLimit,
    checkStorageLimit,
    checkMemberLimit,
  };
}
